import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { APIError, FormFieldConfig, Role } from "../helpers/shared-interfaces";
import { handleValidationErrors, request , showTotalPagination } from "../helpers/utilities";
import { Table, Spin, Button, message, FormInstance } from "antd";
import Card from "../components/Card";
import { NavLink } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import ExportData from "../components/ExportData";
import { PageToolbar } from "../components/PageToolbar";
import { CREATE_ROLE, GET_ROLES } from "../gql/roles";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isFiltering: boolean;
  roles: Role[];
  page: number;
  roleCount: number;
  filteredRoles: Role[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  showNewRoleModal: boolean;
  isCreatingRole: boolean;
  removedField?: IField;
}

export function UserCountRender(text: string, record: Role): JSX.Element {
  return <span>{record.users.length}</span>;
}

export const NewEntityUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  isLoading: boolean;
  field: string;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      name: "name",
      label: "Name",
      inputType: "string",
      placeholder: `Enter ${props.field} Name`,
      rule: "string",
      required: true,
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <Spin spinning={props.isLoading}>
      <BaseForm
        layout="vertical"
        reference={props.reference}
        fieldsToHide={[]}
        fields={fields}
      />
    </Spin>
  );
};

export const columns = [
  {
    title: "Role Name",
    dataIndex: "name",
    key: "Department Name",
    render(text: string, record: Role): JSX.Element {
      return (
        <NavLink to={`/roles/${record.id}`} data-cy="test-role-link">
          {text}
        </NavLink>
      );
    },
  },
  {
    title: "Number of Users",
    dataIndex: "users",
    key: "Number of Users",
    render: UserCountRender,
  },
];

export function RoleTableFactory(
  data: Role[],
  isLoading: boolean,
  count: number,
  page: number,
  nextPage: (page: number) => void,
): JSX.Element {
  const tempData: Role[] = data.map((department: Role): Role => {
    return { ...department, key: department.id };
  });
  return (
    <Table
      loading={isLoading}
      columns={columns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: 25,
        total: count,
        onChange: nextPage,
        showTotal: showTotalPagination,
      }}
    />
  );
}

export default class AllDepartments extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      roles: [],
      page: 1,
      roleCount: 0,
      filteredRoles: [],
      isFiltering: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
      showNewRoleModal: false,
      isCreatingRole: false
    };
    this.reference = React.createRef<FormInstance>();
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getAllRoles = this.getAllRoles.bind(this);
    this.filterRoles = this.filterRoles.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.toggleCreateRoleModal =
      this.toggleCreateRoleModal.bind(this);
  }

  toggleDeleteConfirmationModal(): void {
    this.setState((prevState) => ({
      showDialog: !prevState.showDialog
    }));
  }

  async filterRoles(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.persist();
    this.setState({ filterBy: e.target.value, isFiltering: true });
    const filterBy: string = e.target.value;
    if (filterBy.length === 0) {
      this.setState({ isFiltering: false });
      return;
    }
    const filtered: Role[] = this.state.roles.filter(function (
      role: Role,
    ): boolean {
      if (role.name.toLowerCase().includes(filterBy.toLowerCase())) {
        return true;
      }
      return false;
    });
    this.setState({ filteredRoles: filtered });
  }

  getAllRoles(): void {
    this.setState({ isLoading: true });
    request({
      query: GET_ROLES,
      variables: {
        page: this.state.page ?? 1,
      },
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          roles: data.roles.results,
          roleCount: data.roles.count,
        });
      })
      .catch((error) => {
        message.error("Failed to load roles");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllRoles();
    });
  }

  toggleCreateRoleModal(): void {
    this.setState((prevState) => ({
      showNewRoleModal: !prevState.showNewRoleModal
    }));
  }

  toggleAdvancedSearchModel = (): void => {
    this.setState((prevState) => ({
      showAdvancedSearchModal: !prevState.showAdvancedSearchModal
    }));
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      roles: data.results as Role[],
      selectedFieldsToDisplay: fields,
      page: 1,
      roleCount: data.count
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState({
      removedField: field,
      selectedFieldsToDisplay: fields,
    });

    if (fields.length === 0) this.getAllRoles();
  };

  nextPage(page: number) {
    this.setState({ isLoading: true, page }, (): void => {
      this.getAllRoles();
    });
  }

  createRole = async (): Promise<void> => {
    interface Response {
      data?: {
        createRole?: {
          role?: {
            name: string;
          };
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_ROLE,
        variables: data,
      });

      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.createRole &&
        response.data.data.createRole.role
      ) {
        message.success("Role created sucessfully");
        this.getAllRoles();
      } else {
        message.error("Failed to create role");
      }
    } catch (error) {
      message.error(`Failed to create role - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isCreatingRole: false });
      this.toggleCreateRoleModal();
    }
  };

  render(): JSX.Element | null {
    const {
      roles,
      isFiltering,
      filteredRoles,
      isLoading,
      selectedFieldsToDisplay,
      removedField,
      page,
      roleCount,
      isCreatingRole,
      showNewRoleModal
    } = this.state;
    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="role"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
        />
        <Card>
          <div className="roles-table-container">
            <PageToolbar
              searchValue={this.state.filterBy}
              onSearchChange={this.filterRoles}
              searchPlaceholder="Filter by name..."
              advancedSearch={{
                onOpen: this.toggleAdvancedSearchModel,
                activeFields: selectedFieldsToDisplay,
                onFieldRemove: this.handleOnFieldRemove,
              }}
              actions={
                <EntitledSection entitlement={entitlements.Roles.create}>
                  <ExportData
                    model={"role"}
                    dataArray={this.state.roles}
                  />
                </EntitledSection>
              }
              primaryAction={
                <EntitledSection entitlement={entitlements.Roles.create}>
                  <Button
                    type="primary"
                    onClick={this.toggleCreateRoleModal}
                    data-cy="test-new-role-button"
                  >
                    <PlusOutlined /> Create Role
                  </Button>
                </EntitledSection>
              }
              totalCount={roleCount}
              currentDataCount={roles.length}
              page={page}
              pageSize={25}
              modelName="roles"
            />
            {RoleTableFactory(
              isFiltering ? filteredRoles : roles,
              isLoading,
              roleCount,
              page,
              this.nextPage,
            )}
            {showNewRoleModal &&
              ModalCreator({
                title: "Create Role",
                okType: "primary",
                okText: "Create",
                showModal: showNewRoleModal,
                onOkay: this.createRole,
                toggleModal: this.toggleCreateRoleModal,
                inscription: NewEntityUI({
                  reference: this.reference,
                  isLoading: isCreatingRole,
                  field: "Role",
                }),
              })}
          </div>
        </Card>
      </>
    );
  }
}
